import React, { useState } from 'react';
import { View } from 'react-native';
import { CalcShell, FormulaLine, FormulaNote, NumericField } from '../../../components/shell';
import { DiagramBoilerOutput } from '../../../components/diagrams';
import { usePersistedState } from '../../../hooks/usePersistedState';
import { useUnits } from '../../../context/UnitsContext';

const KEY = 'tabai.calc.hydronicBoilerOutput.v1';

type Fields = { inputBtu: string; efficiency: string; output: string };
const EMPTY: Fields = { inputBtu: '', efficiency: '', output: '' };

/**
 * Boiler Output — Output = Input × (Efficiency / 100)
 * Bidirectional: fill any 2 of 3 fields.
 */
const HydronicBoilerOutputScreen = () => {
  const { unit, toImp, fromImp } = useUnits();
  const [fields, setFields, { clear: clearPersisted }] = usePersistedState<Fields>(KEY, EMPTY);
  const [error, setError] = useState<string | null>(null);
  const [solvedFor, setSolvedFor] = useState<keyof Fields | null>(null);

  const set = (patch: Partial<Fields>) => setFields((prev) => ({ ...prev, ...patch }));

  const calculate = () => {
    setError(null);
    const filled = (Object.values(fields) as string[]).filter((v) => v !== '').length;
    if (filled < 2) { setError('Fill any 2 of the 3 fields to solve for the missing one.'); return; }
    if (filled === 3) { setError('All 3 values are filled — clear one to solve for it.'); return; }

    const input = toImp(fields.inputBtu, 'heatRate');
    const eff = parseFloat(fields.efficiency);
    const out = toImp(fields.output, 'heatRate');

    if (fields.output === '') {
      if (!(input > 0)) { setError('Input must be greater than zero.'); return; }
      if (!Number.isFinite(eff) || eff <= 0 || eff > 100) { setError('Efficiency must be between 0 and 100%.'); return; }
      setSolvedFor('output');
      set({ output: fromImp(input * (eff / 100), 'heatRate', 2) });
    } else if (fields.inputBtu === '') {
      if (!(out > 0)) { setError('Output must be greater than zero.'); return; }
      if (!Number.isFinite(eff) || eff <= 0 || eff > 100) { setError('Efficiency must be between 0 and 100%.'); return; }
      setSolvedFor('inputBtu');
      set({ inputBtu: fromImp(out / (eff / 100), 'heatRate', 2) });
    } else if (fields.efficiency === '') {
      if (!(input > 0)) { setError('Input must be greater than zero.'); return; }
      if (!(out > 0)) { setError('Output must be greater than zero.'); return; }
      const e = (out / input) * 100;
      if (e <= 0 || e > 100) { setError('Resulting efficiency is out of range (0–100%).'); return; }
      setSolvedFor('efficiency');
      set({ efficiency: parseFloat(e.toFixed(2)).toString() });
    }
  };

  const clearAll = () => {
    setError(null);
    setSolvedFor(null);
    void clearPersisted();
  };

  const result = [
    fields.inputBtu && { label: 'Input', value: fields.inputBtu, unit: unit('heatRate'), emphasis: solvedFor === 'inputBtu' },
    fields.efficiency && { label: 'Efficiency', value: fields.efficiency, unit: '%', emphasis: solvedFor === 'efficiency' },
    fields.output && { label: 'Boiler Output', value: fields.output, unit: unit('heatRate'), emphasis: solvedFor === 'output' },
  ].filter(Boolean) as { label: string; value: string; unit?: string; emphasis?: boolean }[];

  return (
    <CalcShell
      title="Boiler Output"
      subtitle="Solve for output, input, or efficiency using Output = Input × (Eff ÷ 100)"
      formula={
        <>
          <FormulaLine>Output = Input × (Efficiency ÷ 100)</FormulaLine>
          <FormulaNote>Input = Input heat rate ({unit('heatRate')}), Efficiency = Boiler Efficiency (%)</FormulaNote>
        </>
      }
      error={error}
      onCalculate={calculate}
      onClear={clearAll}
      result={result.length ? result : null}
      copyFooter="Formula: Output = Input × (Efficiency ÷ 100)"
      diagram={<DiagramBoilerOutput />}
      outputSlots={{
        boiler_input_btuh: fields.inputBtu ? toImp(fields.inputBtu, 'heatRate') : undefined,
        boiler_efficiency_pct: fields.efficiency ? parseFloat(fields.efficiency) : undefined,
        boiler_output_btuh: fields.output ? toImp(fields.output, 'heatRate') : undefined,
      }}
      onPrefill={(values) => {
        const patch: Partial<Fields> = {};
        if (values.boiler_input_btuh != null && !fields.inputBtu) {
          patch.inputBtu = fromImp(values.boiler_input_btuh, 'heatRate', 2);
        }
        if (values.boiler_efficiency_pct != null && !fields.efficiency) {
          patch.efficiency = parseFloat(Number(values.boiler_efficiency_pct).toFixed(2)).toString();
        }
        if (values.boiler_output_btuh != null && !fields.output) {
          patch.output = fromImp(values.boiler_output_btuh, 'heatRate', 2);
        }
        if (Object.keys(patch).length > 0) set(patch);
      }}
    >
      <View style={{ marginBottom: 12 }}>
        <NumericField label="Input" value={fields.inputBtu} onChangeText={(v) => set({ inputBtu: v })} rightText={unit('heatRate')} maxDecimals={2} onSubmitEditing={calculate} />
      </View>
      <View style={{ marginBottom: 12 }}>
        <NumericField label="Efficiency" value={fields.efficiency} onChangeText={(v) => set({ efficiency: v })} rightText="%" maxDecimals={2} onSubmitEditing={calculate} />
      </View>
      <View style={{ marginBottom: 12 }}>
        <NumericField label="Boiler Output" value={fields.output} onChangeText={(v) => set({ output: v })} rightText={unit('heatRate')} maxDecimals={2} onSubmitEditing={calculate} />
      </View>
    </CalcShell>
  );
};

export default HydronicBoilerOutputScreen;
